import { useEffect, useState } from 'react';
import { payApiClient } from '@/api/client';
import type { AdminGatewayDescriptor, Gateway } from '@/api/models';

/**
 * `GET /v1/admin/gateways` — what each gateway needs configured, published by
 * the platform because the platform's own adapters are what decide it. The
 * console renders those field names and encodings; it never keeps a second
 * copy of them, since a stale copy shows as a credential written under a name
 * nothing reads, or written as the wrong bytes — both look configured and fail
 * only at the first real payment.
 *
 * Shared, module-level cache: the answer is identical for every account and
 * every tenant and changes only when the platform is deployed, so the several
 * dialogs that need it fetch it once between them. A failed load is not cached,
 * so reopening a dialog retries.
 */
let inFlight: Promise<AdminGatewayDescriptor[]> | undefined;

function isWellFormed(item: AdminGatewayDescriptor | undefined): item is AdminGatewayDescriptor {
  return (
    typeof item?.gateway === 'string' &&
    typeof item.accountFields === 'object' &&
    item.accountFields !== null &&
    Array.isArray(item.credentialFields)
  );
}

function loadDescriptors(): Promise<AdminGatewayDescriptor[]> {
  inFlight ??= payApiClient
    .listAdminGatewayDescriptors()
    // An API that answers this path with something else entirely (an older
    // deployment whose 404 body parses, a proxy's own JSON) is treated as "no
    // descriptors" — never as a load to retry forever, and never as a
    // half-shaped descriptor the screens would then read fields off.
    .then((response) => (response?.items ?? []).filter(isWellFormed))
    .catch((caught: unknown) => {
      inFlight = undefined;
      throw caught;
    });
  return inFlight;
}

/** Test seam: drops the shared cache so each test starts from a clean fetch. */
export function resetGatewayDescriptorCache() {
  inFlight = undefined;
}

interface GatewayDescriptorState {
  descriptor: AdminGatewayDescriptor | undefined;
  loading: boolean;
  /**
   * The platform did not answer with a descriptor for this gateway — an older
   * API that predates this surface, or a gateway it does not know. Callers fall
   * back to their unguided shape rather than blocking the screen: an
   * administrator with the field names to hand must still be able to work.
   */
  unavailable: boolean;
}

export function useGatewayDescriptor(gateway: Gateway | undefined, enabled = true): GatewayDescriptorState {
  // One settled/unsettled flag rather than "is the list empty?" — an API that
  // answers with no descriptors at all is a settled answer, and re-deriving the
  // question from the data would re-fetch forever.
  const [state, setState] = useState<{ status: 'idle' | 'loading' | 'settled'; descriptors: AdminGatewayDescriptor[] }>({
    status: 'idle',
    descriptors: [],
  });

  // No cancellation on unmount, deliberately: the effect runs twice under
  // React's development double-invoke, and a cleanup that disarmed the first
  // run's handlers would leave the second run's early return with nothing left
  // to settle the state — the dialog would sit on its loading skeleton forever.
  // The request itself is shared and idempotent, so the worst a late resolution
  // can do is set state on a component that is still mounted anyway.
  useEffect(() => {
    if (!enabled || state.status !== 'idle') return;

    setState((prev) => ({ ...prev, status: 'loading' }));
    loadDescriptors()
      .then((items) => setState({ status: 'settled', descriptors: items }))
      .catch(() => setState({ status: 'settled', descriptors: [] }));
  }, [enabled, state.status]);

  const loading = state.status === 'loading';
  const descriptor = gateway ? state.descriptors.find((item) => item.gateway === gateway) : undefined;

  return {
    descriptor,
    loading,
    unavailable: state.status === 'settled' && !descriptor,
  };
}
